import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Check, LayoutDashboard } from "lucide-react";
import { IdeaCard } from "@/components/idea-card";
import { useState } from "react";
import { colors } from "@/lib/colors";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import type { SelectIdea } from "@db/schema";
import { UserMenu } from "@/components/user-menu";
import { useAuth } from "@/contexts/auth";

const TAGS = ["Reduce", "Reform", "Advance", "Expand"] as const;

export default function Home() {
  const [sortBy, setSortBy] = useState("newest");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const { user } = useAuth();

  const { data: ideas, isLoading, error } = useQuery({
    queryKey: ["/api/ideas", sortBy, selectedTags],
    queryFn: async () => {
      const tagsParam = selectedTags.length > 0 ? selectedTags.join(",") : "all";

      try {
        const res = await fetch(`/api/ideas?sort=${sortBy}&tag=${tagsParam}`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        const contentType = res.headers.get('content-type');

        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Failed to fetch ideas: ${errorText}`);
        }

        if (!contentType?.includes('application/json')) {
          throw new Error(`Expected JSON response but got ${contentType}`);
        }

        const data = await res.json();

        return Array.isArray(data) ? data : [];
      } catch (error) {
        throw error;
      }
    },
  });

  if (error) {
    return (
      <div>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h2 className="text-red-700">Error loading ideas</h2>
            <p className="text-red-600">{error instanceof Error ? error.message : 'Unknown error'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-8 mb-8">
          <div className="flex items-center gap-2 flex-shrink-0">
            <img src="/elk-icon.png" alt="Icon" className="h-12 w-12" />
            <h1 className="text-2xl sm:text-3xl font-bold flex gap-2">
              <span style={{ color: colors.unbridledBlue }}>Reduce</span> |
              <span style={{ color: colors.unbridledGray }}>Reform</span> |
              <span style={{ color: colors.unbridledOrange }}>Advance</span> |
              <span style={{ color: colors.unbridledGreen }}>Expand</span>
            </h1>
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <UserMenu />
          </div>
        </div>

        <div className="flex justify-end gap-4 mb-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-[180px] justify-between"
                style={{
                  borderColor: colors.unbridledBlue,
                  color: colors.unbridledBlue
                }}
              >
                {selectedTags.length === 0
                  ? "Filter by tags..."
                  : `${selectedTags.length} tag${selectedTags.length === 1 ? '' : 's'} selected`}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[180px]">
              {TAGS.map((tag) => (
                <DropdownMenuItem
                  key={tag}
                  onSelect={(e) => {
                    e.preventDefault();
                    if (selectedTags.includes(tag)) {
                      setSelectedTags((prev) => prev.filter((t) => t !== tag));
                    } else {
                      setSelectedTags((prev) => [...prev, tag]);
                    }
                  }}
                  className="flex items-center justify-between hover:bg-[#5a8ba7]/10"
                >
                  {tag}
                  {selectedTags.includes(tag) && (
                    <Check className="h-4 w-4" style={{ color: colors.unbridledBlue }} />
                  )}
                </DropdownMenuItem>
              ))}
              {selectedTags.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setSelectedTags([])}
                    className="hover:bg-[#4b4f54]/10"
                    style={{ color: colors.concrete }}
                  >
                    <div className="flex items-center gap-2">
                      <X className="h-4 w-4" />
                      Clear All
                    </div>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-[180px] justify-between"
                style={{
                  borderColor: colors.unbridledBlue,
                  color: colors.unbridledBlue
                }}
              >
                {sortBy === "newest" ? "Newest First" : "Most Voted"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[180px]">
              <DropdownMenuItem
                onSelect={() => setSortBy("newest")}
                className="flex items-center justify-between hover:bg-[#5a8ba7]/10"
              >
                Newest First
                {sortBy === "newest" && (
                  <Check className="h-4 w-4" style={{ color: colors.unbridledBlue }} />
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setSortBy("most-voted")}
                className="flex items-center justify-between hover:bg-[#5a8ba7]/10"
              >
                Most Voted
                {sortBy === "most-voted" && (
                  <Check className="h-4 w-4" style={{ color: colors.unbridledBlue }} />
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[200px] rounded-lg bg-white/50 animate-pulse" />
            ))
          ) : Array.isArray(ideas) && ideas.length > 0 ? (
            ideas.map((idea: SelectIdea) => (
              <IdeaCard key={idea.id} idea={idea} />
            ))
          ) : (
            <div className="col-span-3 text-center py-8 text-gray-500">
              No ideas found. {selectedTags.length > 0 ? "Try adjusting your filters." : "Be the first to submit one!"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}